import { ApiError, api } from './client.ts';

// Mirrors the bot's closed action list. A confirmation is bound to one of
// these plus the parameters of the operation, so a grant obtained for one
// action can never be spent on another.
export const STEP_UP_ACTIONS = {
  CARD_DETAILS_REVEAL: 'CARD_DETAILS_REVEAL',
  CARD_PIN_CHANGE: 'CARD_PIN_CHANGE',
  VAULT_WITHDRAWAL: 'VAULT_WITHDRAWAL',
  SECOND_FACTOR_ENROL: 'SECOND_FACTOR_ENROL',
} as const;

export type StepUpAction = (typeof STEP_UP_ACTIONS)[keyof typeof STEP_UP_ACTIONS];
export type StepUpMethod = 'BIOMETRIC' | 'EMAIL';

// Mirrors the bot's enrolment operations. Adding and removing a device are
// separate operations to it, so the confirmation has to name which one.
export const ENROL_OPERATIONS = {
  ENROL: 'ENROL',
  REMOVE: 'REMOVE',
} as const;

export type EnrolOperation = (typeof ENROL_OPERATIONS)[keyof typeof ENROL_OPERATIONS];

export interface StepUpParams {
  readonly cardId?: string;
  readonly currency?: string;
  readonly toAddress?: string;
  readonly amount?: string;
  readonly deviceId?: string;
  readonly operation?: EnrolOperation;
}

export interface BiometricStart {
  readonly kind: 'BIOMETRIC';
  readonly ceremonyId: string;
  readonly alternatives: readonly StepUpMethod[];
}

export interface EmailStart {
  readonly kind: 'EMAIL';
  readonly ceremonyId: string;
  readonly emailMasked: string;
  readonly alternatives: readonly StepUpMethod[];
  readonly recommendBiometric: boolean;
}

export interface GrantedStart {
  readonly kind: 'GRANTED';
  readonly grant: string;
  readonly expiresAt: string;
}

export type StepUpStart = BiometricStart | EmailStart | GrantedStart;

export interface StepUpGranted {
  readonly grant: string;
  readonly expiresAt: string;
}

export interface BiometricDevice {
  readonly deviceId: string;
  readonly label: string | null;
  readonly createdAt: string;
  readonly lastUsedAt: string | null;
}

export const STEP_UP_CODES = {
  REQUIRED: 'STEP_UP_REQUIRED',
  NO_FACTOR: 'STEP_UP_NO_FACTOR',
  EXPIRED: 'STEP_UP_EXPIRED',
  INVALID: 'STEP_UP_INVALID',
  LOCKED: 'STEP_UP_LOCKED',
} as const;

// True when the bot refused because no confirmation accompanied the request.
// The caller answers by running a ceremony and retrying with the grant.
export const isStepUpRequired = (err: unknown): boolean =>
  err instanceof ApiError && err.code === STEP_UP_CODES.REQUIRED;

export const startStepUp = (action: StepUpAction, params: StepUpParams, method?: StepUpMethod) =>
  api<StepUpStart>('/step-up/start', {
    method: 'POST',
    body: { action, ...params, ...(method !== undefined && { method }) },
  });

export interface StepUpProof {
  readonly code?: string;
  readonly biometricToken?: string;
  readonly deviceId?: string;
}

export const verifyStepUp = (ceremonyId: string, proof: StepUpProof) =>
  api<StepUpGranted>('/step-up/verify', { method: 'POST', body: { ceremonyId, ...proof } });

export const listBiometricDevices = (signal?: AbortSignal) =>
  api<{ readonly devices: readonly BiometricDevice[] }>(
    '/step-up/devices',
    signal !== undefined ? { signal } : {},
  );

// The grant is optional on the wire so the Mini App can run the same
// "call, get refused, confirm, call again" flow it runs for every other
// protected action instead of a special enrolment path. The token comes back
// exactly once, to be handed straight to the Telegram client.
export const enrolBiometricDevice = (deviceId: string, label: string, grant?: string) =>
  api<{ readonly token: string }>('/step-up/biometric', {
    method: 'POST',
    body: { deviceId, label },
    ...(grant !== undefined && { stepUpGrant: grant }),
  });

// Undoing an enrolment whose token the Telegram client would not store. Needs
// no confirmation: the token the enrolment just returned is the proof, and the
// device it names is one the account cannot otherwise use.
export const discardBiometricEnrolment = (deviceId: string, token: string) =>
  api<{ readonly ok: true }>('/step-up/biometric/discard', {
    method: 'POST',
    body: { deviceId, token },
  });

export const removeBiometricDevice = (deviceId: string, grant?: string) =>
  api<{ readonly ok: true }>(`/step-up/biometric/${encodeURIComponent(deviceId)}`, {
    method: 'DELETE',
    ...(grant !== undefined && { stepUpGrant: grant }),
  });
